import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api';
import { formatEuros, localDate, shortDate } from '../format';
import type { Expense, ExpenseCategory, RecurringTemplate, Room } from '../types';

/**
 * Modèles de charges récurrentes (internet, copropriété, assurance…) : saisis
 * une fois, reconduits chaque mois. Le montant du mois reste modifiable après
 * génération, une révision annuelle ne doit pas obliger à recréer le modèle.
 */
function RecurringTemplates({ categories, onChanged }: {
  categories: ExpenseCategory[];
  onChanged: () => Promise<void>;
}) {
  const [templates, setTemplates] = useState<RecurringTemplate[]>([]);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(categories[0]?.code ?? '');
  const [label, setLabel] = useState('');
  const [amountEuros, setAmountEuros] = useState('');
  const [deductible, setDeductible] = useState(true);
  const [startDate, setStartDate] = useState(localDate());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function load(): Promise<void> {
    try {
      const result = await api<{ templates: RecurringTemplate[] }>('/api/admin/finances/recurring-templates');
      setTemplates(result.templates);
    } catch {
      // Silencieux : la liste des charges ponctuelles reste consultable sans elle.
    }
  }

  useEffect(() => { void load(); }, []);

  async function create(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const amountCents = Math.round(Number(amountEuros.replace(',', '.')) * 100);
      await api('/api/admin/finances/recurring-templates', {
        method: 'POST',
        body: JSON.stringify({ category, label: label.trim(), amountCents, deductible, startDate }),
      });
      setLabel('');
      setAmountEuros('');
      await load();
      await onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Création impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function update(template: RecurringTemplate, patch: Partial<Pick<RecurringTemplate, 'amountCents' | 'deductible' | 'active'>> & { dayOfMonth?: number }): Promise<void> {
    setError('');
    try {
      await api(`/api/admin/finances/recurring-templates/${template.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          amountCents: patch.amountCents ?? template.amountCents,
          deductible: patch.deductible ?? template.deductible,
          active: patch.active ?? template.active,
          ...(patch.dayOfMonth !== undefined ? { dayOfMonth: patch.dayOfMonth } : {}),
        }),
      });
      await load();
      await onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Modification impossible.');
    }
  }

  return (
    <div className="recurring-block">
      <div className="section-heading">
        <div><p className="eyebrow">Postes fixes</p><h3>Charges récurrentes ({templates.filter((t) => t.active).length})</h3></div>
        <button type="button" className="text-button" onClick={() => setOpen(!open)}>{open ? 'Fermer' : 'Nouveau modèle'}</button>
      </div>

      {templates.length > 0 && (
        <div className="record-list">
          {templates.map((template) => (
            <article className={`record-card ${template.active ? '' : 'muted'}`} key={template.id}>
              <div className="record-heading">
                <div>
                  <strong>{template.label}</strong>
                  <span className="record-code">{template.category} · depuis {shortDate(template.startDate)}</span>
                </div>
                <input
                  className="inline-amount"
                  inputMode="decimal"
                  defaultValue={(template.amountCents / 100).toFixed(2)}
                  onBlur={(event) => {
                    const cents = Math.round(Number(event.target.value.replace(',', '.')) * 100);
                    if (cents > 0 && cents !== template.amountCents) void update(template, { amountCents: cents });
                  }}
                />
              </div>
              <p className="record-line">
                <label className="check-field"><input type="checkbox" checked={template.deductible} onChange={(event) => void update(template, { deductible: event.target.checked })} /><span>Déductible</span></label>
                <label className="check-field"><input type="checkbox" checked={template.active} onChange={(event) => void update(template, { active: event.target.checked })} /><span>Actif</span></label>
                <label className="day-field">Jour de prélèvement
                  <input
                    type="number" min="1" max="31" className="inline-day"
                    defaultValue={Number(template.startDate.slice(8, 10))}
                    onBlur={(event) => {
                      const day = Math.round(Number(event.target.value));
                      if (day >= 1 && day <= 31 && day !== Number(template.startDate.slice(8, 10))) {
                        void update(template, { dayOfMonth: day });
                      }
                    }}
                  />
                </label>
              </p>
            </article>
          ))}
        </div>
      )}

      {open && (
        <form className="form-grid" onSubmit={(event) => void create(event)}>
          <label>Poste
            <select value={category} onChange={(event) => setCategory(event.target.value)}>
              {categories.map((entry) => <option key={entry.code} value={entry.code}>{entry.label}</option>)}
            </select>
          </label>
          <label>Libellé
            <input value={label} onChange={(event) => setLabel(event.target.value)} required placeholder="Internet, copropriété…" />
          </label>
          <label>Montant mensuel (€)
            <input inputMode="decimal" value={amountEuros} onChange={(event) => setAmountEuros(event.target.value)} required />
          </label>
          <label>Premier prélèvement
            <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} required />
          </label>
          <label className="check-field"><input type="checkbox" checked={deductible} onChange={(event) => setDeductible(event.target.checked)} /><span>Déductible</span></label>
          <div className="span-2 record-actions">
            <button className="button primary compact" disabled={busy}>{busy ? 'Création…' : 'Créer le modèle'}</button>
          </div>
        </form>
      )}
      {error && <div className="inline-message error" role="alert">{error}</div>}
    </div>
  );
}

/** Table récapitulative : total par poste, pour l'année affichée. */
function ExpensesByCategory({ expenses, categories }: { expenses: Expense[]; categories: ExpenseCategory[] }) {
  const totals = new Map<string, number>();
  for (const expense of expenses) {
    totals.set(expense.category, (totals.get(expense.category) ?? 0) + expense.amountCents);
  }
  const rows = [...totals.entries()]
    .map(([code, amountCents]) => ({
      code,
      label: categories.find((entry) => entry.code === code)?.label ?? code,
      group: categories.find((entry) => entry.code === code)?.group ?? '',
      amountCents,
    }))
    .sort((left, right) => right.amountCents - left.amountCents);
  const grandTotal = rows.reduce((sum, row) => sum + row.amountCents, 0);

  if (rows.length === 0) return null;

  return (
    <div className="journal-scroll">
      <table className="journal-table">
        <thead><tr><th scope="col">Poste</th><th scope="col">Groupe</th><th scope="col" className="subtotal">Total</th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.code}>
              <th scope="row">{row.label}</th>
              <td className="muted">{row.group}</td>
              <td className="subtotal">{formatEuros(row.amountCents)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr><th scope="row">Total</th><td /><td className="subtotal">{formatEuros(grandTotal)}</td></tr></tfoot>
      </table>
    </div>
  );
}

/** Formulaire d'édition d'une dépense déjà saisie : mêmes champs que la création. */
function ExpenseEditor({ expense, categories, rooms, onSaved, onCancel, onDeleted }: {
  expense: Expense;
  categories: ExpenseCategory[];
  rooms: Room[];
  onSaved: () => Promise<void>;
  onCancel: () => void;
  onDeleted: () => Promise<void>;
}) {
  const [category, setCategory] = useState(expense.category);
  const [label, setLabel] = useState(expense.label);
  const [amountEuros, setAmountEuros] = useState((expense.amountCents / 100).toFixed(2));
  const [spentOn, setSpentOn] = useState(expense.spentOn);
  const [roomId, setRoomId] = useState(expense.roomId ?? '');
  const [deductible, setDeductible] = useState(expense.deductible);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const amountCents = Math.round(Number(amountEuros.replace(',', '.')) * 100);
      await api(`/api/admin/expenses/${expense.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ roomId: roomId || null, spentOn, category, label: label.trim(), amountCents, deductible }),
      });
      await onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/admin/expenses/${expense.id}`, { method: 'DELETE' });
      await onDeleted();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Suppression impossible.');
      setBusy(false);
    }
  }

  return (
    <form className="form-grid" onSubmit={(event) => void save(event)}>
      <label>Poste
        <select value={category} onChange={(event) => setCategory(event.target.value)}>
          {categories.map((entry) => <option key={entry.code} value={entry.code}>{entry.label}</option>)}
        </select>
      </label>
      <label>Libellé
        <input value={label} onChange={(event) => setLabel(event.target.value)} required />
      </label>
      <label>Montant (€)
        <input inputMode="decimal" value={amountEuros} onChange={(event) => setAmountEuros(event.target.value)} required />
      </label>
      <label>Date
        <input type="date" value={spentOn} onChange={(event) => setSpentOn(event.target.value)} required />
      </label>
      <label>Chambre concernée
        <select value={roomId} onChange={(event) => setRoomId(event.target.value)}>
          <option value="">Toutes / sans objet</option>
          {rooms.map((room) => <option key={room.id} value={room.id}>{room.name}</option>)}
        </select>
      </label>
      <label className="check-field"><input type="checkbox" checked={deductible} onChange={(event) => setDeductible(event.target.checked)} /><span>Déductible</span></label>
      {error && <div className="inline-message error span-2" role="alert">{error}</div>}
      <div className="span-2 record-actions">
        <button type="button" className="text-button danger" disabled={busy} onClick={() => void remove()}>Supprimer</button>
        <button type="button" className="text-button" disabled={busy} onClick={onCancel}>Annuler</button>
        <button className="button primary compact" disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
      </div>
    </form>
  );
}

function ExpenseDocument({ expense, onUploaded }: { expense: Expense; onUploaded: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function upload(file: File): Promise<void> {
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      await api(`/api/admin/expenses/${expense.id}/document`, { method: 'POST', body: form });
      await onUploaded();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Envoi impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="expense-document">
      {expense.documentUrl
        ? <a href={expense.documentUrl} target="_blank" rel="noreferrer">Justificatif</a>
        : (
          <label className="text-button">
            {busy ? 'Envoi…' : 'Joindre un justificatif'}
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              hidden
              disabled={busy}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void upload(file);
                event.target.value = '';
              }}
            />
          </label>
        )}
      {error && <span className="warn-text"> {error}</span>}
    </span>
  );
}

export function ExpensesPanel({ year }: { year: string }) {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  const [category, setCategory] = useState('');
  const [label, setLabel] = useState('');
  const [amountEuros, setAmountEuros] = useState('');
  const [spentOn, setSpentOn] = useState(localDate());
  const [roomId, setRoomId] = useState('');
  const [deductible, setDeductible] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState('');

  async function load(): Promise<void> {
    try {
      const [expensesResult, categoriesResult, roomsResult] = await Promise.all([
        api<{ expenses: Expense[] }>(`/api/admin/expenses?year=${year}`),
        api<{ categories: ExpenseCategory[] }>('/api/admin/finances/categories'),
        api<{ rooms: Room[] }>('/api/admin/rooms'),
      ]);
      setExpenses(expensesResult.expenses);
      setCategories(categoriesResult.categories);
      setRooms(roomsResult.rooms);
      setCategory((current) => current || categoriesResult.categories[0]?.code || '');
      setError('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
    }
  }

  useEffect(() => { void load(); }, [year]);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const amountCents = Math.round(Number(amountEuros.replace(',', '.')) * 100);
      await api('/api/admin/expenses', {
        method: 'POST',
        body: JSON.stringify({ roomId: roomId || null, spentOn, category, label: label.trim(), amountCents, deductible }),
      });
      setLabel('');
      setAmountEuros('');
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  }

  const total = expenses.reduce((sum, expense) => sum + expense.amountCents, 0);

  return (
    <section className="panel">
      <div className="section-heading">
        <div><p className="eyebrow">Sorties</p><h3>Dépenses {year} ({formatEuros(total)})</h3></div>
        <button type="button" className="button compact" onClick={() => setOpen(!open)}>{open ? 'Fermer' : 'Saisir une dépense'}</button>
      </div>

      {open && (
        <form className="form-grid expense-form" onSubmit={(event) => void submit(event)}>
          <label>Poste
            <select value={category} onChange={(event) => setCategory(event.target.value)}>
              {categories.map((entry) => <option key={entry.code} value={entry.code}>{entry.label}</option>)}
            </select>
          </label>
          <label>Libellé
            <input value={label} onChange={(event) => setLabel(event.target.value)} required />
          </label>
          <label>Montant (€)
            <input inputMode="decimal" value={amountEuros} onChange={(event) => setAmountEuros(event.target.value)} required />
          </label>
          <label>Date
            <input type="date" value={spentOn} onChange={(event) => setSpentOn(event.target.value)} required />
          </label>
          <label>Chambre concernée
            <select value={roomId} onChange={(event) => setRoomId(event.target.value)}>
              <option value="">Toutes / sans objet</option>
              {rooms.map((room) => <option key={room.id} value={room.id}>{room.name}</option>)}
            </select>
          </label>
          <label className="check-field"><input type="checkbox" checked={deductible} onChange={(event) => setDeductible(event.target.checked)} /><span>Déductible</span></label>
          <div className="span-2 record-actions">
            <button className="button primary compact" disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer la dépense'}</button>
          </div>
        </form>
      )}

      {error && <div className="inline-message error" role="alert">{error}</div>}

      {/* Trois blocs repliés : à l'échelle d'une année, la liste des dépenses
          ponctuelles à elle seule occupait tout l'écran. */}
      <details className="access-panel" open>
        <summary>Récapitulatif par poste</summary>
        <ExpensesByCategory expenses={expenses} categories={categories} />
      </details>

      <details className="access-panel">
        <summary>Charges récurrentes</summary>
        <RecurringTemplates categories={categories} onChanged={load} />
      </details>

      <details className="access-panel">
        <summary>Dépenses saisies ({expenses.length})</summary>
      {expenses.length === 0 && <div className="empty-state">Aucune dépense pour {year}.</div>}
      <div className="record-list">
        {expenses.map((expense) => (
          <article className="record-card" key={expense.id}>
            {editingId === expense.id ? (
              <ExpenseEditor
                expense={expense}
                categories={categories}
                rooms={rooms}
                onCancel={() => setEditingId('')}
                onSaved={async () => { setEditingId(''); await load(); }}
                onDeleted={async () => { setEditingId(''); await load(); }}
              />
            ) : (
              <>
                <div className="record-heading">
                  <div>
                    <strong>{expense.label}</strong>
                    <span className="record-code">
                      {shortDate(expense.spentOn)} · {expense.category}
                      {expense.roomName ? ` · ${expense.roomName}` : ''}
                      {expense.recurring ? ' · récurrente' : ''}
                      {!expense.deductible ? ' · non déductible' : ''}
                    </span>
                  </div>
                  <strong>{formatEuros(expense.amountCents)}</strong>
                </div>
                <p className="record-line">
                  <ExpenseDocument expense={expense} onUploaded={load} />
                  <button type="button" className="text-button" onClick={() => setEditingId(expense.id)}>Modifier</button>
                </p>
              </>
            )}
          </article>
        ))}
      </div>
      </details>
    </section>
  );
}
